/**
 * A AJUDA DE CADA TELA — o texto que o "?" mostra, e que a Duda pode ler.
 *
 * ─── POR QUE ISTO É UM ARQUIVO DE CÓDIGO, E NÃO UM MANUAL ───────────────────
 * 👑 02/09: *"queria incluir o acesso assistido em cada página, para explicar o
 * que faz cada uma delas... vídeo? aquele que faz zoom no item e mostra passo a
 * passo"*.
 *
 * 🟥 Vídeo foi descartado por MEDIÇÃO, não por gosto: dezenas de commits mexem em
 * telas toda semana. Um vídeo gravado hoje mostra uma tela que daqui a pouco já
 * é outra, e ninguém regrava dezenas de vídeos. O pior é o modo de falhar: a
 * pessoa assiste, faz o que o vídeo manda, e o botão não está mais lá. **A ajuda
 * vira mentira sem ninguém perceber.**
 *
 * Texto ao lado do código falha melhor: quem muda a tela esbarra nele, e o
 * portão (`tests/ajuda-de-cada-tela.test.ts`) reprova tela registrada sem ajuda
 * boa. Adoção por ONDA: tela que não entrou aqui não bloqueia ninguém.
 *
 * ─── COMO ESCREVER UMA ENTRADA ──────────────────────────────────────────────
 * `oQueE`     uma frase. O que a tela É, na língua de quem usa — não na do banco.
 * `vimAqui`   2 a 4 coisas que a pessoa REALMENTE vem fazer aqui, por frequência.
 * `naoFaz`    🔑 o campo que mais evita chamado. Toda tela tem uma vizinha com
 *             que é confundida; dizer isso em voz alta poupa a viagem.
 * `cuidado`   opcional — o que dá errado e não dá pra desfazer.
 *
 * ⚠️ NÃO descreva botão ("clique em Salvar"). Botão muda de lugar e o texto
 * apodrece igual ao vídeo. Descreva o TRABALHO, que muda muito menos.
 */

export interface AjudaDeTela {
  /** título curto que aparece no topo do painel */
  titulo: string;
  oQueE: string;
  vimAqui: string[];
  naoFaz: string;
  cuidado?: string;
}

/**
 * Indexado pela ROTA, não pelo componente: o "?" vive no `Layout` e descobre
 * onde está por `useLocation()`. Assim nenhuma tela precisa ser tocada pra
 * ganhar ajuda — e uma tela nova a ganha escrevendo só aqui.
 *
 * 1ª ONDA (28/10): as telas da Regula vistoriadas ao vivo no redesign.
 */
export const AJUDA_POR_ROTA: Record<string, AjudaDeTela> = {
  "/requisitos-conformidade": {
    titulo: "Requisitos de Conformidade",
    oQueE:
      "O checklist que o sistema usa para avaliar cada documento: a IA propõe os requisitos da norma e você aprova, edita ou descarta.",
    vimAqui: [
      "Conferir o que a IA propôs para um tipo de documento",
      "Aprovar de uma vez os requisitos que estão certos",
      "Ajustar ou tirar um requisito que não se aplica",
    ],
    naoFaz:
      "Não avalia os documentos da sua carteira — isso é a análise de cada unidade. Aqui você só define a régua que a análise vai usar.",
    cuidado:
      "Um requisito que você aprova passa a valer para toda avaliação daquele tipo. Largo demais, ele vira pendência onde não devia.",
  },

  "/a-confirmar": {
    titulo: "A confirmar",
    oQueE:
      "O balde dos documentos que a IA leu com pouca certeza do tipo. Você confirma o tipo, ou troca, e ele entra valendo no acervo da unidade.",
    vimAqui: [
      "Confirmar o tipo que a IA propôs para um documento",
      "Trocar o tipo quando a IA errou a leitura",
      "Abrir o documento para conferir antes de decidir",
    ],
    naoFaz:
      "Não é onde o documento chega — ele já está guardado. Aqui você só dá a palavra final sobre o tipo quando a IA ficou em dúvida.",
    cuidado:
      "Confirmar grava o tipo como conferido por você e recalcula o vencimento. É a sua verdade que passa a valer no acervo.",
  },

  "/faturamento-regula": {
    titulo: "Faturamento Regula",
    oQueE:
      "A geração do faturamento mensal de todas as propostas ativas: diagnóstico, boletos, auditoria com honorário de êxito e sindicatura.",
    vimAqui: [
      "Gerar as faturas do mês de referência",
      "Ver quanto já entrou e o que está vencido",
      "Acompanhar as parcelas de honorário de êxito",
    ],
    naoFaz:
      "Não recebe pagamento nem baixa título — isso é o contas a receber. Aqui você só gera a cobrança, e pode gerar de novo sem duplicar.",
  },

  "/fechamento": {
    titulo: "Resultado",
    oQueE:
      "O quanto o mês rendeu, nas duas verdades que raramente batem: o que foi faturado (competência) e o que de fato entrou no caixa.",
    vimAqui: [
      "Ver se o mês fechou no azul ou no vermelho",
      "Entender por que o faturado e o caixa não batem",
      "Abrir um custo para ver de onde ele veio",
    ],
    naoFaz:
      "Não lança despesa nem receita — isso é feito nos lançamentos. Aqui o resultado só reúne o que já foi registrado e mostra a conta.",
  },

  "/portal-regula": {
    titulo: "Portal do cliente",
    oQueE:
      "A home do cliente: numa olhada, se as unidades dele estão em risco, quanto já voltou de dinheiro e o que depende da assinatura dele.",
    vimAqui: [
      "Ver de imediato se há unidade em risco",
      "Saber quanto a Regula já recuperou de dinheiro",
      "Encontrar o que está esperando o aval do cliente",
    ],
    naoFaz:
      "Não mostra o documento em si nem a conta por dentro — para isso você entra na unidade. Aqui é o resumo do estado, não o detalhe.",
  },
};

/** A ajuda da rota, se existir. Rota sem ajuda → `undefined`, e o "?" some. */
export function ajudaDaRota(pathname: string): AjudaDeTela | undefined {
  return AJUDA_POR_ROTA[pathname];
}

/**
 * As rotas que JÁ têm ajuda escrita. O portão usa isto pra cobrar as que faltam
 * sem precisar adivinhar o que existe.
 */
export const ROTAS_COM_AJUDA = Object.keys(AJUDA_POR_ROTA);
